import { AxiosError } from "axios";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { Button, LinkButton } from "../../components/Button";
import ConfirmModal from "../../components/ConfirmModal";
import * as Fonts from "../../components/Fonts";
import Input from "../../components/Form/Input/input";
import { Form, FormGroup } from "../../components/Form/styles";
import { FreeKeeposLimitModal } from "../../components/FreeKeeposLimitModal";
import { LoadingModal } from "../../components/LoadingModal";
import { PlusOnlyModal } from "../../components/PlusOnlyModal";
import { PlusTag } from "../../components/PlusTag";
import { SubHeader } from "../../components/SubHeader";
import { useToast } from "../../components/Toast";
import ToggleSwitch from "../../components/ToggleSwitch";
import constants from "../../constants";
import { AppState, useAppState } from "../../contexts/App";
import { useSite } from "../../contexts/Site";
import { useCheckUrl } from "../../hooks/CheckUrl";
import { useToggleSiteActive } from "../../hooks/ToggleSiteActive";
import ChevronRightIcon from "../../icons/ChevronRight";
import { Site } from "../../model/site";
import { api } from "../../services/api";
import { ERR_FREE_SITES_LIMIT_REACHED, isApiError } from "../../services/api/errors";
import { publishSite } from "../../services/publishSite";
import CategoriesInputSelect, { isCategoryComplete } from "../Editor/components/CategoriesInputSelect";
import { AdvancedConfigs } from "./components/AdvancedConfigs";
import { Card, CardFooter, Container, RemoveLink, Section, SubscriptionCard, ToggleRow } from "./styles";

const SettingsPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();

  const { appState, setAppState } = useAppState();
  const { site, setSite } = useSite();

  const { isActive, isTogglingActive, toggleActive, showActivateLimitModal, closeActivateLimitModal } =
    useToggleSiteActive();

  const { url, onChangeUrl, urlReady, loadingUrl, urlNotAvailable } = useCheckUrl(
    appState?.editingSiteId as string,
    site?.url || ""
  );

  const [title, setTitle] = useState<string>(site?.title || "");
  const [category, setCategory] = useState<string>(site?.category || "");
  const [categoryOther, setCategoryOther] = useState<string>(site?.categoryOther || "");

  const [hideSignature, setHideSignature] = useState<boolean>(!!site?.hideSignature);

  const [showConfirmRemoveModal, setShowConfirmRemoveModal] = useState(false);
  const [isPublishError, setIsPublishError] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const [showFreeKeeposLimitModal, setShowFreeKeeposLimitModal] = useState(false);
  const [showSignaturePlusModal, setShowSignaturePlusModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  // The field of work stays optional here: an untouched one is always fine (older Keepos may have none, "author"
  // or another legacy key), but once changed, "other" needs its text
  const isCategoryUnchanged = category === (site?.category || "") && categoryOther === (site?.categoryOther || "");
  const isCategoryValid = isCategoryUnchanged || !category || isCategoryComplete(category, categoryOther);
  const isSaveDisabled = !url || !title || !urlReady || !isCategoryValid;

  const isPlus = appState.editingSiteSubscription?.type === "PLUS";

  const goPremiumUrl = `/subscription/features?site=${appState.editingSiteId}`;

  const saveHandler = () => {
    publishSiteHandler();
  };

  const publishSiteHandler = async () => {
    setIsSaving(true);
    setIsPublishError(false);
    setErrorMessage(undefined);

    try {
      const payload = {
        ...site,
        url,
        title,
        category,
        categoryOther: categoryOther.trim() || undefined,
        hideSignature,
      } as Site;
      const publishedSite = await publishSite(
        appState?.editingSiteId as string,
        payload,
        appState.editingSiteSubscription,
        appState.editingSiteDisabled,
        appState.published
      );
      // publishing also replaced the pending draft (and made an inactive Keepo active again)
      if (appState.editingSiteDisabled || appState.editingSiteModified) {
        setAppState({ ...appState, editingSiteDisabled: false, editingSiteModified: false });
      }
      // the brand turned on or off: a toast says what went on the air (a FREE Keepo's look is simplified when it goes on
      // the air, brand included: what was asked in the form isn't always what was published)
      const hidden = !!publishedSite.hideSignature;
      if (!!site?.hideSignature !== hidden) {
        toast.success(t(`pages.settings.form.${hidden ? "signatureHidden" : "signatureShown"}`));
      }
      // Stay on the screen: the save bar disappears as the saved site now matches the form
      setSite(publishedSite);
    } catch (e) {
      if (isApiError(e, ERR_FREE_SITES_LIMIT_REACHED)) {
        setShowFreeKeeposLimitModal(true);
      } else if (e instanceof AxiosError && e.response && e.response.status === 409) {
        setIsPublishError(true);
      } else {
        setErrorMessage(t("pages.settings.errors.generic"));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const remove = async () => {
    setIsRemoving(true);
    setShowConfirmRemoveModal(false);

    try {
      await api.deleteSite(appState?.editingSiteId as string);

      // Back to the list, which no longer has it; `replace` so going back doesn't reopen the deleted Keepo
      setAppState({
        ...appState,
        editingSiteId: undefined,
        notRedirectOnEmptySites: true,
      } as AppState);

      navigate("/sites", { replace: true });
    } catch (e) {
      setIsRemoving(false);
    }
  };

  // `hideSignature` is often absent on the saved site: compare it as a boolean
  const hasUnsavedChanges = () =>
    (site?.url || "") !== url ||
    (site?.title || "") !== title ||
    (site?.category || "") !== category ||
    (site?.categoryOther || "") !== categoryOther ||
    !!site?.hideSignature !== hideSignature;
  const isDirty = hasUnsavedChanges();
  const canSave = isDirty && !isSaveDisabled && !isSaving;

  const discardChanges = () => {
    onChangeUrl(site?.url || "");
    setTitle(site?.title || "");
    setCategory(site?.category || "");
    setCategoryOther(site?.categoryOther || "");
    setHideSignature(!!site?.hideSignature);
    setIsPublishError(false);
    setErrorMessage(undefined);
  };

  const submitHandler = (e: React.FormEvent) => {
    e.preventDefault();
    if (canSave) {
      saveHandler();
    }
  };

  const handleHideSignatureChange = useCallback(() => {
    setHideSignature(hideSignature => !hideSignature);
  }, []);

  // FREE Keepos can't hide the brand: the tag and the locked toggle explain it's a PLUS feature
  const handleHideSignatureClick = useCallback(() => {
    if (!isPlus) {
      setShowSignaturePlusModal(true);
    }
  }, [isPlus]);

  return (
    <>
      {(isSaving || isRemoving) && <LoadingModal />}
      <Container>
        <SubHeader
          title={t("pages.settings.header.title")}
          crumbs={[]}
          hasUnsavedChanges={hasUnsavedChanges}
          rightElement={isPlus && <PlusTag />}
        />

        {/* Takes effect immediately, independent of the form below */}
        <Section>
          <Fonts.Label>{t("pages.settings.sections.status")}</Fonts.Label>
          <Card>
            <ToggleRow>
              <div className="text">
                <h6>{isActive ? t("pages.settings.form.active") : t("pages.settings.form.inactive")}</h6>
                <p className="description">{t("pages.settings.form.activeDescription")}</p>
              </div>
              <ToggleSwitch
                label={t("pages.settings.form.active")}
                value={isActive}
                setValue={toggleActive}
                disabled={isTogglingActive}
              />
            </ToggleRow>
          </Card>
        </Section>

        <Form onSubmit={submitHandler}>
          <Section>
            <Fonts.Label>{t("pages.settings.sections.info")}</Fonts.Label>
            <Card>
              <ToggleRow>
                <div className="label">
                  <h6>{t("pages.settings.form.showSignature")}</h6>
                  {!isPlus && (
                    <button
                      type="button"
                      className="plus"
                      aria-label={t("pages.settings.form.upgrade")}
                      onClick={handleHideSignatureClick}>
                      <PlusTag />
                    </button>
                  )}
                </div>
                <ToggleSwitch
                  label={t("pages.settings.form.showSignature")}
                  value={!hideSignature}
                  setValue={handleHideSignatureChange}
                  disabled={!isPlus}
                  onClick={handleHideSignatureClick}
                />
              </ToggleRow>
              <div className="divider" />
              <FormGroup>
                <Input
                  type="text"
                  id="title"
                  required
                  maxLength={24}
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  label={t("pages.settings.form.title")}
                  autoCapitalize="on"
                />
              </FormGroup>
              <FormGroup>
                <Input
                  type="text"
                  id="url"
                  maxLength={30}
                  ignoreLengthInfo
                  value={url}
                  sufix={`.${constants.nameSite}`}
                  required
                  autoCapitalize="none"
                  loading={loadingUrl}
                  onChange={e => onChangeUrl(e.target.value.replace(/\s/g, ""))}
                  infoMessage={
                    urlNotAvailable || (isPublishError && url) ? t("pages.settings.form.url.notAvailable") : ""
                  }
                  label={t("pages.settings.form.url.label")}
                />
              </FormGroup>
              <CategoriesInputSelect
                id="category"
                value={category}
                other={categoryOther}
                onChange={(value, other) => {
                  setCategory(value);
                  setCategoryOther(other);
                }}
              />
              <CardFooter>
                {errorMessage && <p className="error">{errorMessage}</p>}
                <div className="actions">
                  {isDirty && (
                    <LinkButton onClick={discardChanges}>{t("pages.settings.unsavedBar.discard")}</LinkButton>
                  )}
                  <Button type="INFO" size="MD" disabled={!canSave} onClick={() => canSave && saveHandler()}>
                    {isSaving
                      ? t("pages.settings.actions.save.button.titleSaving")
                      : t("pages.settings.actions.save.button.title")}
                  </Button>
                </div>
              </CardFooter>
            </Card>
          </Section>
          <button type="submit" className="sr-only" tabIndex={-1} aria-hidden="true" />
        </Form>

        <Section>
          <AdvancedConfigs />
        </Section>

        {/* PLUS manages its subscription here; FREE gets the PLUS benefits pitch in the same spot */}
        <Section>
          <Fonts.Label>{t("pages.settings.sections.subscription")}</Fonts.Label>
          <SubscriptionCard
            type="button"
            onClick={() => navigate(isPlus ? `/subscription/detail?site=${appState.editingSiteId}` : goPremiumUrl)}>
            <span className="text">
              <PlusTag>✦ Keepo Plus</PlusTag>
              <span className="title">
                {isPlus ? t("pages.settings.subscription.title") : t("pages.settings.subscription.upgrade.title")}
              </span>
              <span className="description">
                {isPlus
                  ? t("pages.settings.subscription.description")
                  : t("pages.settings.subscription.upgrade.description")}
              </span>
            </span>
            <span className="chevron">
              <ChevronRightIcon width={14} height={14} color="currentColor" />
            </span>
          </SubscriptionCard>
        </Section>

        {!isPlus && (
          <RemoveLink type="button" onClick={() => setShowConfirmRemoveModal(true)}>
            {t("pages.settings.actions.remove.link")}
          </RemoveLink>
        )}

        {showConfirmRemoveModal && (
          <ConfirmModal
            type="DANGER"
            title={t("pages.settings.actions.remove.confirmModal.title")}
            descriptions={[t("pages.settings.actions.remove.confirmModal.description")]}
            cancelTitle={t("pages.settings.actions.remove.confirmModal.cancelButton")}
            confirmButtonTitle={t("pages.settings.actions.remove.confirmModal.confirmButton")}
            onCancel={() => setShowConfirmRemoveModal(false)}
            onConfirm={remove}
          />
        )}
        {showSignaturePlusModal && (
          <PlusOnlyModal
            feature={t("pages.plusBenefits.items.brand.title")}
            onClose={() => setShowSignaturePlusModal(false)}
          />
        )}
        {showActivateLimitModal && (
          <FreeKeeposLimitModal
            i18nKeyPrefix="pages.sites.actions.freeKeeposLimitReached"
            descriptionKey="reactivateDescription"
            onConfirm={() => navigate(goPremiumUrl)}
            onCancel={closeActivateLimitModal}
          />
        )}
        {showFreeKeeposLimitModal && (
          <FreeKeeposLimitModal
            i18nKeyPrefix="pages.editor.publish.freeKeeposLimitReached"
            onConfirm={() => navigate(`/subscription/features?site=${appState.editingSiteId}`)}
            onCancel={() => setShowFreeKeeposLimitModal(false)}
          />
        )}
      </Container>
    </>
  );
};

export default SettingsPage;
